import React from 'react';
import DynamicTable from '@atlaskit/dynamic-table';
import Heading from '@atlaskit/heading';
import WorldIcon from '@atlaskit/icon/glyph/world';
import Image from '@atlaskit/image';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { MST_IMAGE_URL, SLACK_IMAGE_URL } from '../../../../common/constants/index.tsx';
import { useChatToolsIntegrations } from '../../../../services/store/chat-tools/index.tsx';
import { messages } from './messages.tsx';

export const StepThree = () => {
	const { formatMessage } = useIntl();

	const [integrations] = useChatToolsIntegrations();

	if (!integrations.data) {
		throw new Error('StepThree should be rendered when chat tools integrations are loaded');
	}

	const head = {
		cells: [
			{ key: 'integration', content: formatMessage(messages.integration), width: 60 },
			{ key: 'team', content: formatMessage(messages.team), width: 40 },
		],
	};

	const rows = integrations.data.values
		.slice(0, 3)
		.map(({ chatSpaceId, name, chatSpaceType, teamName }) => ({
			key: chatSpaceId,
			cells: [
				{
					key: `${chatSpaceId}-integration`,
					content: (
						<Flex alignItems="center" xcss={[rowStyles, linkStyles]} columnGap="space.100">
							<Image
								width="16"
								height="16"
								src={chatSpaceType === 'slack' ? SLACK_IMAGE_URL : MST_IMAGE_URL}
								alt={chatSpaceType}
							/>
							<Box xcss={integrationNameBoxStyles}>
								<Text as="p" maxLines={1}>
									{name}
								</Text>
							</Box>
						</Flex>
					),
				},
				{
					key: `${chatSpaceId}-team`,
					content: (
						<Flex alignItems="center" columnGap="space.100">
							<WorldIcon size="small" label="" />
							<Text as="p" maxLines={1}>
								{teamName || formatMessage(messages.global)}
							</Text>
						</Flex>
					),
				},
			],
		}));

	return (
		<Flex direction="column" rowGap="space.300">
			<Flex rowGap="space.100" direction="column">
				<Heading size="xlarge">{formatMessage(messages.title)}</Heading>
				<Text>{formatMessage(messages.description)}</Text>
			</Flex>
			{integrations.data.count > 0 && (
				<Flex direction="column" rowGap="space.200">
					<Heading size="xsmall" as="div">
						{formatMessage(messages.chatIntegrationsFoundInOpsgenie, {
							amount: integrations.data.count,
						})}
					</Heading>
					<Box xcss={tableStyles}>
						<DynamicTable isFixedSize head={head} rows={rows} rowsPerPage={3} defaultPage={1} />
						<Box xcss={blanketStyles} />
					</Box>
				</Flex>
			)}
		</Flex>
	);
};

const rowStyles = xcss({ height: '36px' });
const linkStyles = xcss({ color: 'color.link' });
const tableStyles = xcss({
	pointerEvents: 'none',
	userSelect: 'none',
	position: 'relative',
	marginBottom: '0',
});
const blanketStyles = xcss({
	width: '100%',
	height: '100%',
	position: 'absolute',
	background: `linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, ${token('elevation.surface.overlay', '#282E33')} 100%)`,
	// table adds margin-bottom: 24px by default. this is a hack to remove it.
	top: 'space.negative.300',
});
const integrationNameBoxStyles = xcss({
	width: '60%',
});
